import { browserUtil } from '../../../common'
import React from 'react'
import { ChoosePlan } from 'src/component/profile/personal-info/choose-plan'
import { MembershipSkeleton } from 'src/component/profile/personal-info/membership-skeleton'
import { OutlinedButton } from 'src/component/ui/button'
import { Card, CardContent } from 'src/component/ui/card'
import { CheckedSvgIcon } from 'src/component/util/images'
import { useMembership } from 'src/hook/use-membership'
import { cn } from 'src/lib/utils'
import { coreService } from 'src/service/core-service'
import { subscriptionUtil } from 'src/util/subscription-util'
import { timeUtil } from 'src/util/time-util'

export const Membership = (): React.ReactElement => {
  const { isLoading, subscriptions, storeSubscription, hasSubscription } = useMembership()

  const timestampToString = (timestamp: number): string => {
    return timeUtil.stringToDateFormat(new Date(timestamp * 1000).toISOString())
  }

  const gridConfigClasses = isLoading ? 'md:col-span-1' : hasSubscription ? 'md:col-span-1' : 'md:col-span-2'

  const isAppStoreBilling = subscriptionUtil.isAppStoreSubscription(storeSubscription?.store)
  const showStripePortal = storeSubscription?.store === 'stripe'
  const managementText = subscriptionUtil.getManagementText(storeSubscription?.store)

  return (
    <div className={cn('p-10 bg-primary-gray rounded-md align-self-start', gridConfigClasses)} data-nextstep="membership">
      <div className="mb-10 flex flex-col lg:flex-row lg:justify-between lg:items-center">
        <h3 className="font-evogria text-2xl md:text-4xl text-primary uppercase">Membership</h3>
        {!isLoading && !hasSubscription && (
          <p className="text-sm md:text-base font-roboto-condensed text-ivory">No active subscription, choose a plan below </p>
        )}
      </div>
      {(isLoading || hasSubscription) && (
        <h6 className="font-evogria text-sm md:text-base text-ivory uppercase mb-4">Your Subscription</h6>
      )}

      {isLoading ? (
        <MembershipSkeleton />
      ) : hasSubscription ? (
        <>
          {subscriptions.map((subscription) => {
            const terminated = subscriptionUtil.subscriptionIsTerminated(subscription)

            return (
              <Card key={subscription.id} className="mb-10 bg-tertiary-grey border-none">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-evogria text-xl md:text-2xl text-white uppercase">
                      {terminated ? 'Cancelled' : 'Active'} Subscription
                    </h4>
                    {!terminated && <CheckedSvgIcon className="h-6 w-6 text-white" />}
                  </div>

                  {subscription.cancelAt && (
                    <div className="mb-3 p-2 bg-primary/20 rounded">
                      <p className="text-xs md:text-sm font-roboto-condensed text-primary">
                        Subscription will be active until {timestampToString(subscription.cancelAt)}
                      </p>
                    </div>
                  )}

                  {subscription.items.map((item, index) => (
                    <div key={index} className="space-y-2">
                      <div>
                        <span className="text-sm md:text-base font-roboto-condensed font-bold text-ivory min-w-[100px]">
                          Plan:{' '}
                        </span>
                        <span className="text-sm md:text-base font-roboto-condensed text-light-gray">{item.product.name}</span>
                      </div>
                      <div>
                        <span className="text-sm md:text-base font-roboto-condensed font-bold text-ivory min-w-[100px]">
                          Price:{' '}
                        </span>
                        <span className="text-sm md:text-base font-roboto-condensed text-light-gray">
                          {subscriptionUtil.formatSubscriptionItemPrice(item)}
                        </span>
                      </div>
                      <div>
                        <span className="text-sm md:text-base font-roboto-condensed font-bold text-ivory min-w-[100px]">
                          Valid until:{' '}
                        </span>
                        <span className="text-sm md:text-base font-roboto-condensed text-light-gray">
                          {timestampToString(subscription.currentPeriodEnd)}
                        </span>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )
          })}

          {isAppStoreBilling && (
            <div
              className="mb-6 rounded-md p-3 bg-tertiary-grey text-ivory text-xs md:text-sm font-roboto-condensed"
              role="note"
              aria-label={managementText}
            >
              <p>{managementText}</p>
            </div>
          )}

          {showStripePortal && (
            <OutlinedButton
              onClick={async () => {
                browserUtil.redirect(await coreService.createPortalSession())
              }}
            >
              Manage Subscription
            </OutlinedButton>
          )}
        </>
      ) : (
        <ChoosePlan />
      )}
    </div>
  )
}
